import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Bento } from "@/components/ui/box";
import { useTabDraft } from "@/components/ui/box-draft";
import type {
  Blocker,
  Box,
  ListBox,
  Result,
  TaskBox,
} from "@/components/ui/box.schema";
import { PersonRecordButton } from "@/components/people/visit/person-management-actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { useEnrollmentNoticeRecovery } from "@/components/enrollment/EnrollmentNoticeRecovery";
import { academicStructureApi } from "@/lib/api/academicStructure";
import { ACCOUNTS_QUERY_KEY, accountsApi } from "@/lib/api/accounts";
import type { Course } from "@/lib/api/courses";
import {
  enrollmentsApi,
  type AddAuditorBody,
  type RosterPlan,
  type RosterResult,
  type RosterRow,
  type RosterState,
  type RosterStudent,
} from "@/lib/api/enrollments";
import { invalidateRosterDependentQueries } from "@/lib/course-query-keys";
import { ENROLLMENT_STATUS_LABEL } from "@/lib/enrollment-status";
import {
  applicantFileHref,
  personRecordHref,
  type PersonOrigin,
  type AdmissionsOrigin,
} from "@/lib/registrar-origin";
import { isHistoricalTerm } from "@/lib/term-state";
import { personChoice } from "@/lib/person-name";
import {
  keepResult,
  recorded,
  registerCommit,
  recoverRegisterCommit,
  registerRead,
  registerText,
  registerCopy,
  registerPhrases,
  registerCandidate,
} from "@/lib/register-copy";

const owner = { system: "imam" } as const;
const name = (person: { preferred_name: string | null; legal_name: string }) =>
  person.preferred_name || person.legal_name;
type SectionContext = Pick<
  Course,
  | "code"
  | "title"
  | "semester_status"
  | "semester_name"
  | "capacity"
  | "enrolled_count"
>;
type Props = {
  courseId: string;
  section?: SectionContext;
  disabled?: boolean;
  initialStudentId?: string | null;
  initialNoticeId?: string | null;
  onOpenNotice?: (id: string) => void;
  onCloseNotice?: () => void;
  admissionsOrigin?: AdmissionsOrigin | null;
  personOrigin?: PersonOrigin | null;
  presentation?: "setup" | "manage";
};
type RowDraft = {
  version?: string;
  action: "participation" | "withdraw" | "status";
  participation: "credit" | "audit";
  reason: string;
  status: "complete" | "incomplete";
};

export function CourseRosterManager(props: Props) {
  return (
    <RosterForCourse
      key={JSON.stringify([
        props.courseId,
        props.initialStudentId,
        props.admissionsOrigin?.applicantId,
        props.personOrigin?.personId,
      ])}
      {...props}
    />
  );
}

function RosterForCourse({
  courseId,
  section,
  disabled = false,
  initialStudentId = null,
  initialNoticeId = null,
  onOpenNotice,
  onCloseNotice,
  admissionsOrigin,
  personOrigin,
  presentation = "manage",
}: Props) {
  const client = useQueryClient();
  const noticeOpener = useRef<HTMLButtonElement | null>(null);
  const noticeBox = useEnrollmentNoticeRecovery({ noticeId: initialNoticeId, courseId, onClose: () => {
    onCloseNotice?.();
    if (noticeOpener.current?.isConnected) noticeOpener.current.focus();
    else document.querySelector<HTMLElement>('[data-box-id="enrollment-roster"] [data-row] [data-act]')?.focus();
  } });
  const roster = useQuery({
    queryKey: ["course-roster", courseId, "state"],
    queryFn: () => enrollmentsApi.readRoster(courseId),
  });
  const cohorts = useQuery({
    queryKey: ["cohorts", "active"],
    queryFn: () => academicStructureApi.getCohorts({ status: "active" }),
  });
  const [search, setSearch] = useState("");
  const candidates = useQuery({
    queryKey: ["course-roster-candidates", courseId, search, initialStudentId],
    queryFn: () =>
      enrollmentsApi.candidates(courseId, search, 40, initialStudentId),
    enabled: search.trim().length >= 2 || Boolean(initialStudentId),
  });
  type AddDraft = {
    picked: Record<string, RosterStudent>;
    studentIds: string[];
    cohortIds: string[];
    reinstateIds: string[];
    capacityReason: string;
    preview: { result: RosterResult; fingerprint: string; version: string } | null;
  };
  const planKeep = useTabDraft<AddDraft, string>({ box: "add-learners", record: courseId, version: roster.data?.updated_at ?? null,
    initial: { picked: {}, studentIds: initialStudentId ? [initialStudentId] : [], cohortIds: [], reinstateIds: [], capacityReason: "", preview: null } });
  const { picked, studentIds, cohortIds, reinstateIds, capacityReason, preview } = planKeep.value;
  const setPlan = planKeep.set;
  const setters = useMemo(() => {
    const field = <K extends keyof AddDraft,>(key: K) => (next: AddDraft[K] | ((held: AddDraft[K]) => AddDraft[K])) =>
      setPlan(held => ({ ...held, [key]: typeof next === "function" ? (next as (value: AddDraft[K]) => AddDraft[K])(held[key]) : next }));
    return { setPicked: field("picked"), setStudentIds: field("studentIds"), setCohortIds: field("cohortIds"), setReinstateIds: field("reinstateIds"), setCapacityReason: field("capacityReason"), setPreview: field("preview") };
  }, [setPlan]);
  const { setPicked, setStudentIds, setCohortIds, setReinstateIds, setCapacityReason, setPreview } = setters;
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState<Result>({ state: "none" });
  const [carriedOutcome, setCarriedOutcome] = useState<
    "added" | "already" | null
  >(null);
  const [rowDrafts, setRowDrafts] = useState<Record<string, RowDraft>>({});
  const [rowResults, setRowResults] = useState<Record<string, Result>>({});
  const [auditor, setAuditor] = useState<AddAuditorBody>({
    user_id: null,
    legal_name: "",
    email: "",
  });
  const [auditorResult, setAuditorResult] = useState<Result>({ state: "none" });
  const [auditorVersion, setAuditorVersion] = useState<string | undefined>();
  const pendingApply = planKeep.pending;
  const setPendingApply = planKeep.setPending;
  const lastConfirmed = useRef<Record<string, Result>>({});
  const previewGeneration = useRef(0);
  // A partial result recorded the enrollment; only notices failed, so it never holds the next plan (S2A-2).
  const locked = Boolean(pendingApply) || result.state === "unknown";
  const closed = isHistoricalTerm(
    roster.data?.term_status ?? section?.semester_status,
  );
  const additionsDisabled = disabled || closed;
  const plan = useMemo<RosterPlan>(
    () => ({
      student_ids: studentIds,
      cohort_ids: cohortIds,
      reinstate_enrollment_ids: reinstateIds,
      capacity_override_reason: capacityReason.trim() || null,
    }),
    [studentIds, cohortIds, reinstateIds, capacityReason],
  );
  const fingerprint = JSON.stringify(plan);
  const latestFingerprint = useRef(fingerprint);
  latestFingerprint.current = fingerprint;
  const currentPreview =
    preview?.fingerprint === fingerprint &&
    preview.version === roster.data?.updated_at
      ? preview.result
      : null;
  const refresh = async () => {
    invalidateRosterDependentQueries(client, courseId);
    await roster.refetch();
  };
  const changed = () => {
    previewGeneration.current += 1;
    setPreview(null);
    setPreviewError(null);
    setResult({ state: "none" });
  };
  // A person edited beside this roster can change eligibility without changing
  // the course version. Keep the selection, but require a fresh preview.
  useEffect(() => client.getQueryCache().subscribe(event => {
    if (!locked && event.type === "updated" && event.action.type === "invalidate"
      && ["course-roster", "course-roster-candidates"].includes(String(event.query.queryKey[0]))
      && event.query.queryKey[1] === courseId) {
      previewGeneration.current += 1;
      setPreview(null);
    }
  }), [client, courseId, locked, setPreview]);
  const toggle = (
    id: string,
    values: string[],
    set: (ids: string[]) => void,
  ) => {
    set(
      values.includes(id)
        ? values.filter((value) => value !== id)
        : [...values, id],
    );
    changed();
  };
  useEffect(() => {
    const carried = candidates.data?.find(
      (person) => person.student_id === initialStudentId,
    );
    if (carried)
      setPicked((held) =>
        held[carried.student_id]
          ? held
          : { ...held, [carried.student_id]: carried },
      );
  }, [candidates.data, initialStudentId, setPicked]);
  const selected = (person: RosterStudent) =>
    person.enrollment_id &&
    person.enrollment_status &&
    person.enrollment_status !== "enrolled"
      ? reinstateIds.includes(person.enrollment_id)
      : studentIds.includes(person.student_id);
  const choose = (person: RosterStudent) => {
    setPicked((held) => ({ ...held, [person.student_id]: person }));
    if (
      person.enrollment_id &&
      person.enrollment_status &&
      person.enrollment_status !== "enrolled"
    )
      toggle(person.enrollment_id, reinstateIds, setReinstateIds);
    else toggle(person.student_id, studentIds, setStudentIds);
  };
  const runPreview = async () => {
    if (previewing || locked) return;
    const generation = ++previewGeneration.current;
    const submitted = fingerprint;
    setPreviewing(true);
    setPreviewError(null);
    try {
      const current = await roster.refetch();
      if (current.isError || !current.data?.updated_at)
        throw new Error(registerText.readTheCurrentRosterBeforeApplyingAPlan);
      const version = current.data.updated_at;
      const answer = await enrollmentsApi.preview(courseId, plan);
      if (
        generation === previewGeneration.current &&
        latestFingerprint.current === submitted &&
        version
      )
        setPreview({ result: answer, fingerprint: submitted, version });
    } catch (error) {
      if (generation === previewGeneration.current)
        setPreviewError(
          error instanceof Error
            ? error.message
            : registerText.thePlanCouldNotBeCheckedYourChoicesAreKept,
        );
    } finally {
      setPreviewing(false);
    }
  };
  const additionBlockers = (): Blocker[] => {
    if (additionsDisabled)
      return [
        {
          kind: "missing",
          part: "who",
          sentence: closed
            ? registerText.thisTermIsClosedItsRosterRemainsReadableAddingAnd
            : registerText.addingLearnersIsUnavailableInThisView,
        },
      ];
    if (!roster.data?.updated_at || roster.isError)
      return [
        {
          kind: "missing",
          part: "who",
          sentence: registerText.readTheCurrentRosterBeforeApplyingAPlan,
        },
      ];
    if (!studentIds.length && !cohortIds.length && !reinstateIds.length)
      return [
        {
          kind: "missing",
          part: "who",
          sentence: registerText.chooseAPersonOrCohortToAdd,
        },
      ];
    if (!currentPreview)
      return [
        {
          kind: "missing",
          part: "plan-preview",
          sentence: registerText.previewThisExactPlanBeforeApplyingIt,
        },
      ];
    if (
      currentPreview.capacity != null &&
      currentPreview.enrolled_after > currentPreview.capacity &&
      !capacityReason.trim()
    )
      return [
        {
          kind: "missing",
          part: "capacity",
          sentence:
            registerText.thisPlanExceedsTheCreditSeatLimitGiveAReason,
        },
      ];
    if (!currentPreview.added.length && !currentPreview.reinstated.length)
      return [
        {
          kind: "missing",
          part: "who",
          sentence:
            registerText.noEligibleRosterChangesAreInThisPlanReviewEach,
        },
      ];
    return [];
  };
  const confirmApply = async (
    value: RosterResult | { batch_id: string },
  ): Promise<Result> => {
    const applied =
      "added" in value
        ? value
        : await enrollmentsApi.batch(courseId, value.batch_id);
    await refresh();
    if ((admissionsOrigin?.studentId ?? personOrigin?.studentId) === initialStudentId) {
      if (
        [...applied.added, ...applied.reinstated].some(
          (person) => person.student_id === initialStudentId,
        )
      )
        setCarriedOutcome("added");
      else if (
        applied.already_enrolled.some(
          (person) => person.student_id === initialStudentId,
        )
      )
        setCarriedOutcome("already");
    }
    if (applied.delivery?.state === "unknown")
      return {
        state: "unknown",
        cap: registerText.enrollmentRecordedNoticesNotConfirmed,
        sentence:
          registerText.enrollmentIsRecordedCheckTheExistingNoticesBeforeSendingAgain,
        requestId: applied.command_key!,
        check: {
          verb: registerText.checkNotices,
          note: registerText.readsTheOriginalBatchAndItsDeliveryRecords,
          run: async () =>
            confirmApply(
              await enrollmentsApi.batch(courseId, applied.batch_id!),
            ),
        },
      };
    if (applied.delivery?.state === "partial")
      return {
        state: "partial",
        at: new Date().toISOString(),
        requestId: applied.command_key!,
        went: applied.delivery.succeeded,
        failed: applied.delivery.failed,
        remains: [registerText.enrollmentRecordedStartAnotherPlan],
        retry: {
          verb: registerText.retryFailedNotices,
          note: registerText.enrollmentIsAlreadyRecordedOnlyTheFailedNoticesAreRetried,
          run: async () => {
            try {
              if (!Object.keys(applied.delivery?.retry_versions ?? {}).length)
                return confirmApply(await enrollmentsApi.batch(courseId, applied.batch_id!));
              return await confirmApply(await enrollmentsApi.retryBatch(
                courseId,
                applied.batch_id!,
                applied.command_key!,
                applied.delivery!.retry_versions!,
              ));
            } catch {
              return confirmApply({ ...applied, delivery: { ...applied.delivery!, state: "unknown" } });
            }
          },
        },
      };
    const receipt = recorded([
      ...applied.added.map((person) => registerPhrases.added(name(person))),
      ...applied.reinstated.map((person) => registerPhrases.reinstated(name(person))),
      registerText.enrollmentAndAcademicHistoryArePreservedNoticeAcceptanceDoesNot,
    ]);
    lastConfirmed.current["add-learners"] = receipt;
    return receipt;
  };
  const storeApply = (value: Result) => {
    setResult(value);
    if (value.state === "confirmed" || value.state === "refused") setPendingApply(null);
    // The plan is applied: its draft and pending send end, and the receipt with Retry stays in this tab.
    if (value.state === "partial") { planKeep.end(); lastConfirmed.current["add-learners"] = value; }
  };
  const sendingApply = useRef(new Set<string>());
  const restoredApply = pendingApply && !sendingApply.current.has(pendingApply) && result.state === "none" ? recoverRegisterCommit<RosterResult | { batch_id: string }>(pendingApply, confirmApply) : null;
  const applyResult = restoredApply?.state === "unknown" ? { ...restoredApply, check: { ...restoredApply.check, run: async () => keepResult(await restoredApply.check.run(), storeApply) } } : result;
  const add: TaskBox<RosterState> = {
    kind: "task",
    id: "add-learners",
    job: registerText.chooseAndReviewAnExactEnrollmentPlan,
    label: registerText.addLearners,
    owner,
    read: registerRead(roster, registerText.roster),
    fold: initialStudentId || reinstateIds.length ? "never" : "choice",
    detail: () => applyResult.state === "unknown" ? applyResult.sentence : registerText.choosePeopleCheckThePlanThenApplyItOnce,
    state: () => ({
      class: result.state === "confirmed" ? "done" :
        studentIds.length + cohortIds.length + reinstateIds.length
          ? "draft"
          : "waiting",
      word: result.state === "confirmed" ? registerText.learnersAdded : currentPreview ? registerText.planChecked : registerText.choosePeople,
    }),
    exits: result.state === "confirmed" ? [{ label: registerText.addMoreLearners, reopens: true }] : undefined,
    act: applyResult.state === "unknown" ? { does: "check", verb: applyResult.check.verb } : { does: "open", verb: registerText.addLearners },
    candidate: {
      eligible: !additionsDisabled,
      rank: null,
      blocked: additionsDisabled ? (additionBlockers()[0] ?? null) : null,
    },
    draft: Boolean(studentIds.length + cohortIds.length + reinstateIds.length),
    result: applyResult,
    parts: [
      {
        id: "who",
        keep: planKeep,
        label: registerText.whoToAdd,
        state: { class: "draft", word: registerText.personChoice },
        markedByBox: true,
        owner,
        fold: "never",
        draft: "browser",
        render: () => (
          <div className="grid gap-4">
            <div>
              <Label htmlFor="roster-search">{registerText.searchNameOrSID}</Label>
              <Input
                id="roster-search"
                disabled={locked}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {search.trim().length < 2 && !initialStudentId && (
              <p>
                {registerText.enterAtLeast2CharactersANameOrEmailNever}</p>
            )}
            {candidates.isFetching && (
              <p role="status">{registerText.readingMatchingPeople}</p>
            )}
            {candidates.isError && (
              <p role="alert">
                {registerText.peopleCouldNotBeRead}{" "}
                <button type="button" onClick={() => void candidates.refetch()}>
                  {registerText.retrySearch}</button>
              </p>
            )}
            <div className="grid gap-2" aria-label={registerText.matchingPeople}>
              {(candidates.data ?? []).map((person) => (
                <div key={person.student_id} className="flex min-w-0 flex-wrap items-start gap-2">
                <label className="flex min-w-0 flex-1 items-start gap-2">
                  <input
                    type="checkbox"
                    disabled={locked || person.enrollment_status === "enrolled"}
                    checked={selected(person)}
                    onChange={() => choose(person)}
                  />
                  <span>
                    {name(person)} · {person.sid} ·{" "}
                    {person.cohort_name ?? registerText.noCohort}
                    {person.enrollment_status
                      ? ` · ${person.enrollment_status === "enrolled" ? registerText.alreadyEnrolled : registerText.reinstate2}`
                      : ""}
                  </span>
                </label>
                <PersonRecordButton personId={person.person_id} name={name(person)} from="add-learners" />
                </div>
              ))}
            </div>
            {search.trim().length >= 2 && candidates.data?.length === 0 && (
              <p>{registerText.noMatchingPeople}</p>
            )}
            {Object.values(picked)
              .filter(
                (person) =>
                  selected(person) &&
                  !candidates.data?.some(
                    (candidate) => candidate.student_id === person.student_id,
                  ),
              )
              .map((person) => (
                <label key={person.student_id}>
                  <input
                    type="checkbox"
                    disabled={locked}
                    checked
                    onChange={() => choose(person)}
                  />{" "}
                  {name(person)} · {person.sid}
                </label>
              ))}
            {reinstateIds
              .filter(
                (id) =>
                  !Object.values(picked).some(
                    (person) => person.enrollment_id === id,
                  ),
              )
              .map((id) => (
                <p key={id}>
                  {name(roster.data!.rows.find((row) => row.id === id)!)} {registerText.reinstate}</p>
              ))}
            <fieldset disabled={locked}>
              <legend>{registerText.cohorts}</legend>
              {cohorts.isError ? (
                <p>
                  {registerText.cohortsAreUnavailable}{" "}
                  <button type="button" onClick={() => void cohorts.refetch()}>
                    {registerText.retryCohorts}</button>
                </p>
              ) : cohorts.isPending ? (
                <p>{registerText.readingCohorts}</p>
              ) : (
                cohorts.data.map((cohort) => (
                  <label key={cohort.id} className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={cohortIds.includes(cohort.id)}
                      onChange={() =>
                        toggle(cohort.id, cohortIds, setCohortIds)
                      }
                    />
                    {cohort.label} · {cohort.active_student_count} {registerText.activeLearners}</label>
                ))
              )}
            </fieldset>
          </div>
        ),
      },
      {
        id: "capacity",
        label: registerText.creditSeats2,
        state: { class: "quiet", word: registerText.capacity },
        owner,
        fold: "never",
        draft: "browser",
        render: () => (
          <div>
            <Label htmlFor="roster-capacity-reason">
              {registerText.reasonForExceedingCapacity}</Label>
            <Input
              id="roster-capacity-reason"
              disabled={locked}
              value={capacityReason}
              onChange={(event) => {
                setCapacityReason(event.target.value);
                changed();
              }}
            />
            <p>
              {registerText.onlyNeededIfThePlanExceedsTheCreditSeatLimit}</p>
          </div>
        ),
      },
      {
        id: "plan-preview",
        label: registerText.planPreview,
        markedByBox: currentPreview ? true : undefined,
        state: {
          class: currentPreview ? "done" : "waiting",
          word: currentPreview ? registerText.checked : registerText.notChecked,
        },
        owner,
        fold: "never",
        draft: "none",
        render: () => (
          <div className="grid justify-items-start gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={
                previewing ||
                locked ||
                additionsDisabled ||
                (!studentIds.length &&
                  !cohortIds.length &&
                  !reinstateIds.length)
              }
              data-act="check"
              onClick={() => void runPreview()}
            >
              {previewing ? registerText.checkingPlan : registerText.previewPlan}
            </Button>
            {previewError && <p role="alert">{previewError}</p>}
            {currentPreview && (
              <>
                <p>
                  {registerText.creditSeats}{currentPreview.enrolled_before} {registerText.to}{" "}
                  {currentPreview.enrolled_after}
                  {currentPreview.capacity == null
                    ? registerText.noLimit
                    : registerPhrases.capacity(currentPreview.capacity)}
                  .
                </p>
                {(
                  [
                    [registerText.alreadyEnrolled, currentPreview.already_enrolled],
                    [registerText.notCurrentlyActive, currentPreview.inactive],
                    [
                      registerText.explicitReinstatementRequired,
                      currentPreview.withdrawn_requires_reinstatement,
                    ],
                  ] as const
                ).map(
                  ([label, people]) =>
                    people.length > 0 && (
                      <p key={label}>
                        {label}: {people.map(name).join(", ")}{registerText.unchanged}</p>
                    ),
                )}
                {currentPreview.warnings.map((warning) => (
                  <p key={`${warning.code}-${warning.student.student_id}`}>
                    {name(warning.student)}: {warning.message}
                  </p>
                ))}
              </>
            )}
          </div>
        ),
      },
    ],
    commit: {
      verb: registerText.applyEnrollment,
      cap: registerText.whenYouApplyThisPlan,
      effects: () =>
        currentPreview
          ? [
              ...currentPreview.added.map((person) => ({
                id: person.student_id,
                sentence: `Add ${name(person)} · ${person.sid}`,
              })),
              ...currentPreview.reinstated.map((person) => ({
                id: person.student_id,
                sentence: registerPhrases.reinstateEffect(name(person), person.sid),
              })),
              {
                id: "notices",
                sentence:
                  currentPreview.notice_effect?.kind === "attempt_on_apply"
                    ? registerPhrases.noticeEffect(currentPreview.notice_effect.count)
                    : currentPreview.notice_effect?.kind ===
                        "deferred_until_publication"
                      ? registerText.deferInitialEnrollmentNoticesUntilTheCourseIsPublished
                      : registerText.createNoNewInitialEnrollmentNotices,
              },
            ]
          : [],
      blockers: additionBlockers,
      run: async (key) => {
        sendingApply.current.add(key);
        setPendingApply(key);
        try {
          return keepResult(
            await registerCommit(
              key,
              () => enrollmentsApi.apply(courseId, plan, key, preview!.version),
              confirmApply,
            ),
            storeApply,
          );
        } finally { sendingApply.current.delete(key); }
      },
    },
  };
  const list: ListBox<RosterState, RosterRow> = {
    kind: "list",
    id: "enrollment-roster",
    job: registerText.readAndCorrectRecordedEnrollments,
    label: registerText.roster,
    owner,
    read: registerRead(roster, registerText.roster),
    fold: "never",
    detail: (value) =>
      value.rows.length
        ? registerText.originalEnrollmentProvenanceAndAcademicHistoryStayRecorded
        : registerText.noLearnersOnThisRoster,
    openDetail: () =>
      closed
        ? registerText.thisTermIsClosedWithdrawalAndStatusCorrectionsRemainAvailable
        : undefined,
    state: (value) => ({
      class: "quiet",
      word: value.rows.length
        ? registerText.recordedEnrollments
        : registerText.noLearnersOnThisRoster,
    }),
    figures: (value) => [
      { value: value.rows.length, unit: registerText.learners },
      {
        value: value.rows.filter(
          (row) =>
            row.status === "enrolled" && row.participation_kind === "credit",
        ).length,
        unit: registerText.credit2,
      },
      {
        value: value.rows.filter((row) => row.participation_kind === "audit")
          .length,
        unit: registerText.auditors,
      },
    ],
    rows: (value) => value.rows,
    row: {
      key: (row) => row.id,
      title: name,
      controls: (row) => <PersonRecordButton personId={row.person_id} name={name(row)} from="enrollment-roster" />,
      state: (row) => ({
        class: row.status === "enrolled" ? "done" : "quiet",
        word: ENROLLMENT_STATUS_LABEL[row.status],
      }),
      facts: (row) =>
        `${row.sid} · ${row.participation_kind === "credit" ? registerText.credit : registerText.audit}`,
      act: () => ({
        does: "open",
        verb:
          presentation === "setup" ? registerText.readEnrollment : registerText.manageEnrollment,
      }),
      detail: (row) => (
        <div className="grid justify-items-start gap-2">
          <p>
            {registerText.originalSource}{" "}
            {row.source_kind ? registerCopy.enrollmentSources[row.source_kind] : registerText.notRecorded}
            {row.source_cohort_id
              ? ` · ${cohorts.data?.find((cohort) => cohort.id === row.source_cohort_id)?.label ?? registerText.cohortNameUnavailable}`
              : ""}
            .
          </p>
          {row.withdrawal_reason && (
            <p>{registerText.lastRecordedWithdrawal}{row.withdrawal_reason}</p>
          )}
          {row.initial_notice_id && onOpenNotice && (
            <Button
              variant="outline"
              onClick={(event) => { noticeOpener.current = event.currentTarget; onOpenNotice(row.initial_notice_id!); }}
            >
              {registerText.openEnrollmentNotice}</Button>
          )}
          {row.status !== "enrolled" && !additionsDisabled && (
            <Button
              variant="outline"
              onClick={() => {
                if (!reinstateIds.includes(row.id)) {
                  setReinstateIds((held) => [...held, row.id]);
                  changed();
                }
                document
                  .getElementById("add-learners")
                  ?.scrollIntoView({ block: "start" });
              }}
            >
              {registerText.reviewReinstatementInAddLearners}</Button>
          )}
        </div>
      ),
      task: (row) => {
        if (presentation === "setup") return undefined;
        const draft = rowDrafts[row.id] ?? {
          version: row.updated_at,
          action: "participation",
          participation: row.participation_kind,
          reason: "",
          status: row.status === "complete" ? "incomplete" : "complete",
        };
        const set = (next: Partial<RowDraft>) => {
          setRowDrafts((held) => ({
            ...held,
            [row.id]: { ...draft, ...next },
          }));
          setRowResults((held) => ({ ...held, [row.id]: { state: "none" } }));
        };
        const rowLocked = rowResults[row.id]?.state === "unknown";
        const conflict = Boolean(
          rowDrafts[row.id] && draft.version !== row.updated_at,
        );
        const full =
          draft.action === "participation" &&
          row.participation_kind === "audit" &&
          draft.participation === "credit" &&
          section?.capacity != null &&
          section.enrolled_count >= section.capacity;
        const blockers = (): Blocker[] => {
          if (conflict)
            return [
              {
                kind: "conflict",
                part: row.id,
                sentence:
                  registerText.thisEnrollmentChangedChooseWhichDraftToKeep,
              },
            ];
          const reason = disabled
            ? registerText.enrollmentChangesAreUnavailableInThisView
            : !row.updated_at || roster.isError
              ? registerText.readTheLatestEnrollmentBeforeChangingIt
              : draft.action === "participation" && closed
                ? registerText.thisTermIsClosedParticipationStaysRecorded
                : draft.action === "participation" && row.status === "withdrawn"
                  ? registerText.reviewReinstatementBeforeChangingParticipation
                  : draft.action === "withdraw" && row.status === "withdrawn"
                    ? registerText.thisEnrollmentIsAlreadyWithdrawn
                    : (draft.action === "withdraw" || full) &&
                        !draft.reason.trim()
                      ? draft.action === "withdraw"
                        ? registerText.giveAWithdrawalReason
                        : registerText.theCreditSeatLimitIsFullGiveACapacityReason
                      : null;
          return reason
            ? [{ kind: "missing", part: row.id, sentence: reason }]
            : [];
        };
        return {
          result: rowResults[row.id] ?? { state: "none" },
          render: () => (
            <fieldset disabled={rowLocked} id={row.id} className="grid gap-3">
              {conflict && !rowLocked && (
                <ConflictNotice
                  object={registerText.enrollment}
                  message={registerText.thisEnrollmentChangedAfterYouBeganThisDraft}
                  facts={[
                    {
                      label: registerText.currentParticipation,
                      theirs: row.participation_kind,
                    },
                    {
                      label: registerText.currentStatus,
                      theirs: ENROLLMENT_STATUS_LABEL[row.status],
                    },
                    {
                      label: registerText.yourParticipation,
                      theirs: draft.participation,
                    },
                    {
                      label: registerText.yourStatusChoice,
                      theirs: ENROLLMENT_STATUS_LABEL[draft.status],
                    },
                  ]}
                  keepMineLabel={registerText.keepMyDraftOnTheNewerEnrollment}
                  loadTheirsLabel={registerText.loadTheNewerEnrollment}
                  onKeepMine={() => set({ version: row.updated_at })}
                  onLoadTheirs={() => {
                    setRowDrafts((held) => {
                      const next = { ...held };
                      delete next[row.id];
                      return next;
                    });
                    setRowResults((held) => ({
                      ...held,
                      [row.id]: { state: "none" },
                    }));
                  }}
                />
              )}

              <div>
                <Label htmlFor={`${row.id}-action`}>{registerText.change}</Label>
                <NativeSelect
                  id={`${row.id}-action`}
                  value={draft.action}
                  onChange={(event) =>
                    set({ action: event.target.value as RowDraft["action"] })
                  }
                >
                  <option value="participation">{registerText.participation}</option>
                  <option value="withdraw">{registerText.withdraw}</option>
                  <option value="status">{registerText.statusCorrection}</option>
                </NativeSelect>
              </div>
              {draft.action === "participation" ? (
                <div>
                  <Label htmlFor={`${row.id}-participation`}>
                    {registerText.participation}</Label>
                  <NativeSelect
                    id={`${row.id}-participation`}
                    value={draft.participation}
                    onChange={(event) =>
                      set({
                        participation: event.target.value as "credit" | "audit",
                      })
                    }
                  >
                    <option value="credit">{registerText.credit}</option>
                    <option value="audit">{registerText.audit}</option>
                  </NativeSelect>
                </div>
              ) : draft.action === "status" ? (
                <div>
                  <Label htmlFor={`${row.id}-status`}>{registerText.enrollmentStatus}</Label>
                  <NativeSelect
                    id={`${row.id}-status`}
                    value={draft.status}
                    onChange={(event) =>
                      set({ status: event.target.value as RowDraft["status"] })
                    }
                  >
                    <option value="complete">{registerText.complete}</option>
                    <option value="incomplete">{registerText.incomplete}</option>
                  </NativeSelect>
                </div>
              ) : null}
              {(draft.action === "withdraw" || full) && (
                <div>
                  <Label htmlFor={`${row.id}-reason`}>
                    {draft.action === "withdraw"
                      ? registerText.withdrawalReason
                      : registerText.reasonForExceedingCapacity}
                  </Label>
                  <Textarea
                    id={`${row.id}-reason`}
                    required
                    aria-invalid={!draft.reason.trim() || undefined}
                    value={draft.reason}
                    onChange={(event) => set({ reason: event.target.value })}
                  />
                </div>
              )}
            </fieldset>
          ),
          commit: {
            verb:
              draft.action === "withdraw"
                ? registerText.withdraw
                : draft.action === "participation"
                  ? registerText.saveParticipation
                  : registerText.correctStatus,
            cap: registerPhrases.enrollmentChange(name(row)),
            effects: () => [
              {
                id: row.id,
                sentence:
                  draft.action === "withdraw"
                    ? registerPhrases.withdrawEffect(name(row), draft.reason)
                    : draft.action === "participation"
                      ? registerPhrases.enrollmentEffect(row.participation_kind, draft.participation)
                      : registerPhrases.enrollmentEffect(ENROLLMENT_STATUS_LABEL[row.status], ENROLLMENT_STATUS_LABEL[draft.status]),
              },
            ],
            blockers,
            revises: {
              part: row.id,
              unchanged: () =>
                draft.action === "participation"
                  ? row.participation_kind === draft.participation
                  : draft.action === "status"
                    ? row.status === draft.status
                    : false,
            },
            run: async (key) =>
              keepResult(
                await registerCommit(
                  key,
                  () =>
                    enrollmentsApi.patch(
                      row.id,
                      draft.action === "withdraw"
                        ? {
                            status: "withdrawn",
                            withdrawal_reason: draft.reason.trim(),
                          }
                        : draft.action === "participation"
                          ? {
                              participation_kind: draft.participation,
                              capacity_override_reason:
                                draft.reason.trim() || undefined,
                              idempotency_key: key,
                            }
                          : { status: draft.status },
                      { key, version: draft.version! },
                    ),
                  async () => {
                    await refresh();
                    setRowDrafts((held) => {
                      // A confirmation ends only the draft that was sent.
                      // Edits made while its response settles remain a draft.
                      if (held[row.id] !== draft) return held;
                      const next = { ...held };
                      delete next[row.id];
                      return next;
                    });
                    return recorded([
                      registerText.enrollmentUpdatedOriginalProvenanceAndAcademicRecordsPreserved,
                    ]);
                  },
                ),
                (value) => {
                  setRowResults((held) => ({ ...held, [row.id]: value }));
                  if (value.state === "refused") void refresh();
                },
              ),
          },
        };
      },
    },
  };
  const auditorConflict = Boolean(auditorVersion && auditorVersion !== roster.data?.updated_at);
  const auditorBlocked: Blocker[] = auditorConflict
    ? [{ kind: "conflict", part: "auditor-person", sentence: registerText.chooseHowToUseTheNewerRosterBeforeAddingThis }] : additionsDisabled
    ? [
        {
          kind: "missing",
          part: "auditor-person",
          sentence: registerText.addingAuditorsIsUnavailableForThisTermOrView,
        },
      ]
    : !auditor.legal_name.trim()
      ? [
          {
            kind: "missing",
            part: "auditor-person",
            sentence: registerText.giveThePersonSLegalName,
          },
        ]
      : !roster.data?.updated_at || roster.isError
        ? [
            {
              kind: "missing",
              part: "auditor-person",
              sentence: registerText.readTheCurrentRosterBeforeAddingAnAuditor,
            },
          ]
        : [];
  const addAuditor: TaskBox<RosterState> = {
    kind: "task",
    id: "add-auditor",
    job: registerText.enrollADeliberatelySelectedStaffOrExternalAuditor,
    label: registerText.addAnAuditor,
    owner,
    read: list.read,
    fold: "choice",
    detail: () =>
      registerText.auditParticipationUsesNoCreditSeatAndCreatesNoFinal,
    state: () => ({
      class: auditorResult.state === "confirmed" ? "done" : auditor.legal_name ? "draft" : "waiting",
      word: auditorResult.state === "confirmed" ? registerText.auditorAdded : registerText.notAdded,
    }),
    exits: auditorResult.state === "confirmed" ? [{ label: registerText.addAnotherAuditor, reopens: true }] : undefined,
    act: { does: "open", verb: registerText.addAnAuditor },
    candidate: {
      eligible: !additionsDisabled,
      rank: null,
      blocked: additionsDisabled ? (auditorBlocked[0] ?? null) : null,
    },
    draft: Boolean(auditor.legal_name || auditor.email),
    result: auditorResult,
    parts: [
      {
        id: "auditor-person",
        label: registerText.person,
        state: { class: "draft", word: registerText.notAdded },
        markedByBox: true,
        owner,
        fold: "never",
        draft: "browser",
        render: () => (
          <>
          {auditorConflict && <ConflictNotice object={registerText.roster2} message={registerText.theRosterChangedYourAuditorDetailsAreKept}
            facts={[{ label: registerText.currentLearners, theirs: String(roster.data?.rows.length ?? "Unavailable") }]}
            keepMineLabel={registerText.keepMyAuditorOnTheNewerRoster} loadTheirsLabel={registerText.loadTheNewerRoster}
            onKeepMine={() => setAuditorVersion(roster.data?.updated_at)}
            onLoadTheirs={() => { setAuditor({ user_id: null, legal_name: "", email: "" }); setAuditorVersion(undefined); }} />}
          <AuditorPerson
            value={auditor}
            disabled={auditorResult.state === "unknown"}
            set={(value) => {
              setAuditor(value);
              setAuditorVersion((held) => held ?? roster.data?.updated_at);
              setAuditorResult({ state: "none" });
            }}
          />
          </>
        ),
      },
    ],
    commit: {
      verb: registerText.addAuditor,
      cap: registerText.whenYouAddThisAuditor,
      effects: () => [
        {
          id: "auditor-person",
          sentence: registerPhrases.auditorEffect(auditor.legal_name),
        },
      ],
      blockers: () => auditorBlocked,
      run: async (key) =>
        keepResult(
          await registerCommit(
            key,
            () =>
              enrollmentsApi.addAuditor(courseId, auditor, {
                key,
                version: auditorVersion!,
              }),
            async () => {
              await refresh();
              setAuditor({ user_id: null, legal_name: "", email: "" });
              setAuditorVersion(undefined);
              return recorded([
                registerText.auditorAddedNoCreditSeatOrFinalGradeIsCreated,
              ]);
            },
          ),
          (value) => {
            setAuditorResult(value);
            if (value.state === "confirmed") lastConfirmed.current["add-auditor"] = value;
            if (value.state === "refused") void refresh();
          },
        ),
    },
  };
  const boxes = [add, list, addAuditor, noticeBox].map((box) =>
    box.candidate
      ? {
          ...box,
          candidate: registerCandidate(box.candidate, box.id, roster.isError ? undefined : roster.data?.ranks),
        }
      : box,
  ) as Box[];
  return (
    <div className="grid gap-4">
      {personOrigin && <div className="space-y-1 text-ui">
        {carriedOutcome && <p role="status">{carriedOutcome === "added" ? `Added to ${section?.code || "this course"}.` : "Already on this roster."}</p>}
        <Link to={personRecordHref(personOrigin)}>{candidates.data?.find(person => person.student_id === personOrigin.studentId) ? `Return to ${name(candidates.data.find(person => person.student_id === personOrigin.studentId)!)}'s record` : "Return to student record"}</Link>
      </div>}
      {admissionsOrigin && carriedOutcome && (
        <Link to={applicantFileHref(admissionsOrigin)}>
          {registerText.returnToApplicantFile}</Link>
      )}
      <Bento
        id={`registrar-roster-${courseId}`}
        journey="registrar-roster"
        boxes={boxes}
        onOpenChange={(id, open) => {
          if (id === "add-learners") {
            if (open && result.state === "confirmed") setResult({ state: "none" });
            else if (!open && !studentIds.length && !cohortIds.length && !reinstateIds.length && result.state === "none" && lastConfirmed.current[id]) setResult(lastConfirmed.current[id]);
          } else if (id === "add-auditor") {
            if (open && auditorResult.state === "confirmed") setAuditorResult({ state: "none" });
            else if (!open && !auditor.legal_name && !auditor.email && auditorResult.state === "none" && lastConfirmed.current[id]) setAuditorResult(lastConfirmed.current[id]);
          }
        }}
      />
    </div>
  );
}

function AuditorPerson({
  value,
  set,
  disabled,
}: {
  value: AddAuditorBody;
  set: (value: AddAuditorBody) => void;
  disabled: boolean;
}) {
  const accounts = useQuery({
    queryKey: ACCOUNTS_QUERY_KEY,
    queryFn: () => accountsApi.list(),
  });
  return (
    <fieldset disabled={disabled} className="grid gap-3">
      <p>
        {registerText.chooseAnExistingPersonExplicitlyNewExternalPeopleRemainAccountless}</p>
      <div>
        <Label htmlFor="auditor-person-select">{registerText.person}</Label>
        <NativeSelect
          id="auditor-person-select"
          value={value.user_id ?? "external"}
          onChange={(event) => {
            const person = accounts.data?.find(
              (entry) => entry.user_id === event.target.value,
            );
            set(
              person
                ? {
                    user_id: person.user_id,
                    legal_name:
                      person.display_name?.trim() ||
                      person.email ||
                      person.user_id,
                    email: person.email || "",
                  }
                : { user_id: null, legal_name: "", email: "" },
            );
          }}
        >
          <option value="external">{registerText.newExternalPerson}</option>
          {accounts.data
            ?.filter((person) => person.is_active)
            .map((person) => (
              <option key={person.user_id} value={person.user_id}>
                {personChoice(person.display_name, person.email)}
              </option>
            ))}
        </NativeSelect>
      </div>
      {accounts.isError && (
        <p role="alert">
          {registerText.existingPeopleCouldNotBeRead}{" "}
          <button type="button" onClick={() => void accounts.refetch()}>
            {registerText.retryPeople}</button>
        </p>
      )}
      <div>
        <Label htmlFor="auditor-legal-name">{registerText.legalName}</Label>
        <Input
          id="auditor-legal-name"
          required
          aria-invalid={!value.legal_name.trim() || undefined}
          value={value.legal_name}
          onChange={(event) =>
            set({ ...value, legal_name: event.target.value })
          }
        />
      </div>
      <div>
        <Label htmlFor="auditor-email">{registerText.emailOptional}</Label>
        <Input
          id="auditor-email"
          type="email"
          value={value.email ?? ""}
          onChange={(event) => set({ ...value, email: event.target.value })}
        />
      </div>
    </fieldset>
  );
}
